import React, { useState } from 'react';
import { ShieldCheckIcon, PlusIcon, EditIcon, Trash2Icon, CheckIcon, XIcon, AlertCircleIcon, UsersIcon, ShieldIcon } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { StatsCard } from '../components/ui/StatsCard';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { Role, Permissions } from '../types';
const PERMISSION_LABELS: Record<keyof Permissions, {
  label: string;
  desc: string;
}> = {
  dashboard: {
    label: 'Panel Principal',
    desc: 'Acceso al panel principal'
  },
  dashboard_cobros: {
    label: '↳ Cobros en Panel',
    desc: 'Ver estadísticas de cobros en el dashboard'
  },
  dashboard_ventas: {
    label: '↳ Ventas en Panel',
    desc: 'Ver estadísticas de ventas en el dashboard'
  },
  dashboard_visitas: {
    label: '↳ Visitas en Panel',
    desc: 'Ver visitas recientes y estadísticas de visitas'
  },
  dashboard_graficos: {
    label: '↳ Gráficos en Panel',
    desc: 'Ver gráficos de rendimiento y distribución'
  },
  dashboard_pedidos: {
    label: '↳ Pedidos en Panel',
    desc: 'Ver widget de pedidos pendientes'
  },
  dashboard_equipo: {
    label: '↳ Equipo Comercial',
    desc: 'Ver resumen del equipo comercial'
  },
  dashboard_productividad: {
    label: '↳ Productividad',
    desc: 'Ver meta diaria y progreso de productividad'
  },
  dashboard_entregas: {
    label: '↳ Panel de Entregas',
    desc: 'Ver sección de entregas (repartidor). Solo ve pedidos empacados.'
  },
  visits: {
    label: 'Historial Visitas',
    desc: 'Ver visitas registradas'
  },
  'new-visit': {
    label: 'Nueva Visita',
    desc: 'Crear y registrar visitas'
  },
  planificacion: {
    label: 'Planificación',
    desc: 'Gestionar planificación de rutas y visitas'
  },
  collections: {
    label: 'Cobros',
    desc: 'Ver y gestionar cobros'
  },
  sales: {
    label: 'Ventas',
    desc: 'Ver y gestionar ventas'
  },
  pedidos: {
    label: 'Pedidos',
    desc: 'Gestionar órdenes de compra'
  },
  cuentas_cobrar: {
    label: 'Cuentas x Cobrar',
    desc: 'Gestionar facturas y cobros'
  },
  facturas_dejadas: {
    label: 'Facturas Dejadas',
    desc: 'Gestionar facturas dejadas en farmacias'
  },
  descuento_pronto_pago: {
    label: 'Descuento Pronto Pago',
    desc: 'Aplicar y gestionar descuentos por pronto pago'
  },
  inventario: {
    label: 'Inventario',
    desc: 'Ver productos en stock'
  },
  inventario_editar: {
    label: '↳ Editar Inventario',
    desc: 'Crear, editar y eliminar productos'
  },
  pedidos_editar: {
    label: '↳ Editar Pedidos',
    desc: 'Editar y eliminar pedidos de otros usuarios'
  },
  caja_vacia: {
    label: 'Caja Vacía',
    desc: 'Gestionar compras de cajas vacías'
  },
  reports: {
    label: 'Reportes',
    desc: 'Acceder a informes'
  },
  map: {
    label: 'Mapa GPS',
    desc: 'Ver ubicaciones en mapa'
  },
  clients: {
    label: 'Farmacias',
    desc: 'Ver y gestionar farmacias'
  },
  users: {
    label: 'Usuarios',
    desc: 'Gestionar usuarios del sistema'
  },
  roles: {
    label: 'Roles y Permisos',
    desc: 'Gestionar roles y accesos'
  },
  devoluciones: {
    label: 'Devoluciones',
    desc: 'Gestionar productos devueltos y notas de crédito'
  },
  ofertas: {
    label: 'Ofertas e Impulsos',
    desc: 'Gestionar productos destacados y promociones'
  },
  comisiones: {
    label: 'Comisiones',
    desc: 'Ver y gestionar comisiones por venta y cobro'
  },
  bitacora: {
    label: 'Bitácora',
    desc: 'Ver registro de actividad del sistema'
  },
  settings: {
    label: 'Configuración',
    desc: 'Acceder a configuración del sistema'
  },
  settings_perfil: {
    label: '↳ Perfil',
    desc: 'Ver y editar foto de perfil'
  },
  settings_seguridad: {
    label: '↳ Seguridad',
    desc: 'Cambiar contraseña de la cuenta'
  },
  settings_notificaciones: {
    label: '↳ Notificaciones',
    desc: 'Configurar preferencias de alertas'
  },
  settings_integraciones: {
    label: '↳ Integraciones',
    desc: 'Gestionar Google Maps y otras integraciones'
  }
};
const PERMISSION_KEYS = Object.keys(PERMISSION_LABELS) as (keyof Permissions)[];
const DEFAULT_PERMISSIONS: Permissions = {
  dashboard: true,
  dashboard_cobros: true,
  dashboard_ventas: true,
  dashboard_visitas: true,
  dashboard_graficos: true,
  dashboard_pedidos: true,
  dashboard_equipo: false,
  dashboard_productividad: true,
  dashboard_entregas: false,
  visits: true,
  'new-visit': true,
  planificacion: true,
  collections: true,
  sales: true,
  pedidos: true,
  cuentas_cobrar: false,
  facturas_dejadas: false,
  descuento_pronto_pago: false,
  inventario: false,
  inventario_editar: false,
  pedidos_editar: false,
  caja_vacia: false,
  devoluciones: false,
  ofertas: false,
  comisiones: false,
  reports: false,
  map: false,
  clients: false,
  users: false,
  roles: false,
  bitacora: false,
  settings: false,
  settings_perfil: true,
  settings_seguridad: true,
  settings_notificaciones: true,
  settings_integraciones: false
};
const ROLE_COLORS: Record<string, string> = {
  Administrador: 'bg-violet-100 text-violet-700 border-violet-200',
  Supervisor: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  Vendedor: 'bg-emerald-100 text-emerald-700 border-emerald-200'
};
export function RolesPage() {
  const {
    roles,
    users,
    addRole,
    updateRole,
    deleteRole
  } = useApp();
  const {
    addActivityLog
  } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [activeTab, setActiveTab] = useState<'table' | 'cards'>('table');
  const [formData, setFormData] = useState({
    nombre: '',
    permisos: {
      ...DEFAULT_PERMISSIONS
    }
  });
  const resetForm = () => {
    setFormData({
      nombre: '',
      permisos: {
        ...DEFAULT_PERMISSIONS
      }
    });
    setEditingRole(null);
    setSubmitError('');
  };
  const openCreateModal = () => {
    resetForm();
    setIsModalOpen(true);
  };
  const openEditModal = (role: Role) => {
    setEditingRole(role);
    setFormData({
      nombre: role.nombre,
      permisos: {
        ...role.permisos
      }
    });
    setSubmitError('');
    setIsModalOpen(true);
  };
  const handleSubmit = async () => {
    if (!formData.nombre.trim()) {
      setSubmitError('El nombre del rol es requerido');
      return;
    }
    setIsSubmitting(true);
    setSubmitError('');
    try {
      if (editingRole) {
        const result = await updateRole(editingRole.id, formData);
        if (!result.success) throw new Error(result.error);
        addActivityLog('Editó rol', `Rol: ${formData.nombre}`);
      } else {
        const result = await addRole(formData);
        if (!result.success) throw new Error(result.error);
        addActivityLog('Creó rol', `Rol: ${formData.nombre}`);
      }
      setIsModalOpen(false);
      resetForm();
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Error al guardar el rol');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleDelete = async (role: Role) => {
    const usersWithRole = users.filter(u => u.rolId === role.id);
    if (usersWithRole.length > 0) {
      alert(`No puedes eliminar este rol porque ${usersWithRole.length} usuario(s) lo tienen asignado.`);
      return;
    }
    if (!confirm(`¿Eliminar el rol "${role.nombre}"?`)) return;
    await deleteRole(role.id);
    addActivityLog('Eliminó rol', `Rol: ${role.nombre}`);
  };
  const togglePermission = (key: keyof Permissions) => {
    setFormData(prev => ({
      ...prev,
      permisos: {
        ...prev.permisos,
        [key]: !prev.permisos[key]
      }
    }));
  };
  const getUserCountForRole = (roleId: string) => users.filter(u => u.rolId === roleId && u.activo).length;
  return <div className="space-y-6" data-id="element-3244">
      <PageHeader title="Roles y Permisos" subtitle="Configuración de acceso al sistema" icon={<ShieldIcon className="w-6 h-6" data-id="element-3246" />} data-id="element-3245">
        <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-3248" />} onClick={openCreateModal} data-id="element-3247">
          Nuevo Rol
        </Button>
      </PageHeader>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 animate-stagger-1" data-id="element-3249">
        <StatsCard title="Total Roles" value={roles.length} icon={<ShieldCheckIcon className="w-5 h-5" data-id="element-3251" />} variant="primary" data-id="element-3250" />
        {roles.slice(0, 3).map(role => <StatsCard key={role.id} title={role.nombre} value={getUserCountForRole(role.id)} subtitle="usuarios" icon={<UsersIcon className="w-5 h-5" data-id="element-3253" />} variant="default" data-id="element-3252" />)}
      </div>

      {/* Tab toggle */}
      <div className="flex gap-1 p-1 bg-slate-100 rounded-xl w-fit animate-stagger-2" data-id="element-3254">
        <button onClick={() => setActiveTab('table')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'table' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-3255">
          Tabla de Permisos
        </button>
        <button onClick={() => setActiveTab('cards')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'cards' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-3256">
          Vista por Rol
        </button>
      </div>

      {/* TABLE VIEW */}
      {activeTab === 'table' && <Card padding="none" className="animate-stagger-3" data-id="element-3257">
          <div className="overflow-x-auto" data-id="element-3258">
            <table className="w-full" data-id="element-3259">
              <thead data-id="element-3260">
                <tr className="bg-slate-50/80 border-b border-slate-100" data-id="element-3261">
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-64 font-heading" data-id="element-3262">
                    Permiso
                  </th>
                  {roles.map(role => <th key={role.id} className="px-4 py-4 text-center text-xs font-semibold uppercase tracking-wider" data-id="element-3263">
                      <div className="flex flex-col items-center gap-1" data-id="element-3264">
                        <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold border ${ROLE_COLORS[role.nombre] || 'bg-slate-100 text-slate-700 border-slate-200'}`} data-id="element-3265">
                          {role.nombre}
                        </span>
                        <span className="text-slate-400 font-normal normal-case" data-id="element-3266">
                          {getUserCountForRole(role.id)} usuarios
                        </span>
                      </div>
                    </th>)}
                  <th className="px-4 py-4 text-center text-xs font-semibold text-slate-500 uppercase" data-id="element-3267">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-3268">
                {PERMISSION_KEYS.map((key, idx) => <tr key={key} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'} data-id="element-3269">
                    <td className="px-6 py-4" data-id="element-3270">
                      <p className="text-sm font-medium text-slate-900" data-id="element-3271">
                        {PERMISSION_LABELS[key].label}
                      </p>
                      <p className="text-xs text-slate-500" data-id="element-3272">
                        {PERMISSION_LABELS[key].desc}
                      </p>
                    </td>
                    {roles.map(role => <td key={role.id} className="px-4 py-4 text-center" data-id="element-3273">
                        {role.permisos[key] ? <div className="inline-flex items-center justify-center w-8 h-8 bg-[#d5e0f7] rounded-lg" data-id="element-3274">
                            <CheckIcon className="w-4 h-4 text-[#2952C4]" data-id="element-3275" />
                          </div> : <div className="inline-flex items-center justify-center w-8 h-8 bg-slate-100 rounded-lg" data-id="element-3276">
                            <XIcon className="w-4 h-4 text-slate-400" data-id="element-3277" />
                          </div>}
                      </td>)}
                    <td className="px-4 py-4" data-id="element-3278" />
                  </tr>)}
                <tr className="bg-slate-50 border-t-2 border-slate-200" data-id="element-3279">
                  <td className="px-6 py-4 text-sm font-semibold text-slate-700" data-id="element-3280">
                    Acciones
                  </td>
                  {roles.map(role => <td key={role.id} className="px-4 py-4 text-center" data-id="element-3281">
                      <div className="flex items-center justify-center gap-1" data-id="element-3282">
                        <button onClick={() => openEditModal(role)} className="p-2 text-slate-500 hover:text-[#2952C4] hover:bg-[#eef2fc] rounded-lg transition-colors" data-id="element-3283">
                          <EditIcon className="w-4 h-4" data-id="element-3284" />
                        </button>
                        <button onClick={() => handleDelete(role)} className="p-2 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-3285">
                          <Trash2Icon className="w-4 h-4" data-id="element-3286" />
                        </button>
                      </div>
                    </td>)}
                  <td data-id="element-3287" />
                </tr>
              </tbody>
            </table>
          </div>
        </Card>}

      {/* CARDS VIEW */}
      {activeTab === 'cards' && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-stagger-3" data-id="element-3288">
          {roles.map(role => <Card key={role.id} className="flex flex-col" data-id="element-3289">
              <div className="flex items-start justify-between mb-4" data-id="element-3290">
                <div data-id="element-3291">
                  <span className={`inline-block px-3 py-1.5 rounded-lg text-sm font-semibold border ${ROLE_COLORS[role.nombre] || 'bg-slate-100 text-slate-700 border-slate-200'}`} data-id="element-3292">
                    {role.nombre}
                  </span>
                  <p className="text-sm text-slate-500 mt-2" data-id="element-3293">
                    {getUserCountForRole(role.id)} usuarios activos •{' '}
                    {Object.values(role.permisos).filter(Boolean).length}{' '}
                    permisos
                  </p>
                </div>
                <div className="flex gap-1" data-id="element-3294">
                  <button onClick={() => openEditModal(role)} className="p-2 text-slate-400 hover:text-[#2952C4] hover:bg-[#eef2fc] rounded-lg transition-colors" data-id="element-3295">
                    <EditIcon className="w-4 h-4" data-id="element-3296" />
                  </button>
                  <button onClick={() => handleDelete(role)} className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-3297">
                    <Trash2Icon className="w-4 h-4" data-id="element-3298" />
                  </button>
                </div>
              </div>
              <div className="flex-1 space-y-2" data-id="element-3299">
                {PERMISSION_KEYS.map(key => <div key={key} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0" data-id="element-3300">
                    <span className="text-sm text-slate-600" data-id="element-3301">
                      {PERMISSION_LABELS[key].label}
                    </span>
                    {role.permisos[key] ? <CheckIcon className="w-4 h-4 text-[#2952C4] flex-shrink-0" data-id="element-3302" /> : <XIcon className="w-4 h-4 text-slate-300 flex-shrink-0" data-id="element-3303" />}
                  </div>)}
              </div>
            </Card>)}
        </div>}

      {/* Modal */}
      <Modal isOpen={isModalOpen} onClose={() => {
      setIsModalOpen(false);
      resetForm();
    }} title={editingRole ? `Editar: ${editingRole.nombre}` : 'Nuevo Rol'} data-id="element-3304">
        <ModalBody data-id="element-3305">
          <div className="space-y-5" data-id="element-3306">
            {submitError && <div className="flex items-start gap-3 p-3 bg-rose-50 border border-rose-200 rounded-xl" data-id="element-3307">
                <AlertCircleIcon className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" data-id="element-3308" />
                <p className="text-sm text-rose-700" data-id="element-3309">{submitError}</p>
              </div>}
            <Input label="Nombre del Rol" placeholder="Ej. Supervisor de Zona" value={formData.nombre} onChange={e => setFormData({
            ...formData,
            nombre: e.target.value
          })} data-id="element-3310" />
            <div data-id="element-3311">
              <label className="block text-sm font-medium text-slate-700 mb-3" data-id="element-3312">
                Permisos de Acceso
              </label>
              <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-3313">
                {PERMISSION_KEYS.map((key, idx) => <label key={key} className={`flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 transition-colors ${idx < PERMISSION_KEYS.length - 1 ? 'border-b border-slate-100' : ''}`} data-id="element-3314">
                    <div data-id="element-3315">
                      <p className="text-sm font-medium text-slate-700" data-id="element-3316">
                        {PERMISSION_LABELS[key].label}
                      </p>
                      <p className="text-xs text-slate-400" data-id="element-3317">
                        {PERMISSION_LABELS[key].desc}
                      </p>
                    </div>
                    <div className="relative inline-flex items-center cursor-pointer ml-4 flex-shrink-0" data-id="element-3318">
                      <input type="checkbox" className="sr-only peer" checked={formData.permisos[key]} onChange={() => togglePermission(key)} data-id="element-3319" />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#aac1ef] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2952C4]" data-id="element-3320"></div>
                    </div>
                  </label>)}
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter data-id="element-3321">
          <Button variant="secondary" onClick={() => {
          setIsModalOpen(false);
          resetForm();
        }} data-id="element-3322">
            Cancelar
          </Button>
          <Button onClick={handleSubmit} isLoading={isSubmitting} data-id="element-3323">
            {editingRole ? 'Guardar Cambios' : 'Crear Rol'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>;
}